import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { ImageBlockData } from '../../../../models/courses';
import { resolveMediaUrl } from './mediaUrl';
import styles from './ImageBlock.module.css';

interface ImageBlockProps {
  data: ImageBlockData;
  onChange: (_data: ImageBlockData) => void;
  onUpload: (_file: File) => Promise<string>;
  disabled?: boolean;
}

const UploadIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 16V4m0 0L8 8m4-4l4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ReplaceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 5V1L7 6l5 5V7a5 5 0 11-5 5H5a7 7 0 107-7z" />
  </svg>
);

export const ImageBlock: React.FC<ImageBlockProps> = ({
  data,
  onChange,
  onUpload,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    if (!disabled && !uploading) {
      inputRef.current?.click();
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const url = await onUpload(file);
      onChange({ ...data, src: url });
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      upload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) {
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      upload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  };

  return (
    <div className={styles.wrap}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={handleFile}
        disabled={disabled || uploading}
      />

      {data.src ? (
        <div
          className={cn(styles.preview, { [styles.previewOver]: dragOver })}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
        >
          <img className={styles.image} src={resolveMediaUrl(data.src)} alt="" />
          <div
            className={cn(styles.overlay, {
              [styles.uploadingOverlay]: uploading,
            })}
          >
            {uploading ? (
              <span className={styles.spinner} />
            ) : (
              <button
                type="button"
                className={styles.replaceBtn}
                onClick={openPicker}
                disabled={disabled}
              >
                <ReplaceIcon />
                Заменить
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={cn(styles.dropzone, { [styles.dropzoneOver]: dragOver })}
          onClick={openPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
        >
          {uploading ? (
            <span className={styles.spinner} style={{ borderTopColor: '#0C7670' }} />
          ) : (
            <>
              <span className={styles.dropIcon}>
                <UploadIcon />
              </span>
              <span className={styles.dropTitle}>Загрузить изображение</span>
              <span className={styles.dropHint}>
                нажмите или перетащите файл сюда
              </span>
            </>
          )}
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.label}>Ссылка при нажатии (баннер)</span>
        <input
          className={styles.input}
          value={data.link ?? ''}
          onChange={(e) => onChange({ ...data, link: e.target.value || undefined })}
          disabled={disabled}
          placeholder="Необязательно — например, ссылка на PDF"
        />
      </div>
    </div>
  );
};
